/**
 * Single source of truth for the embed player's option API.
 *
 * Every option the player understands is declared in `EMBED_OPTION_META`
 * with its canonical name, URL-parameter alias, default value, and kind.
 * Three consumers read from this map and therefore stay in sync by
 * construction:
 * - `buildEmbedSrc(baseUrl, opts)` → iframe URL
 * - `useEmbedSnippet` composable → `data-*` attributes on the `<script>` tag
 * - `loaders/main.ts` → reads those `data-*` attributes back into options
 *
 * Naming rule:
 * - Option key (TS / JS) = camelCase Vue prop name (e.g. `showChapters`).
 * - URL param name = snake_case of the key (e.g. `show_chapters`), with
 *   no prefix stripping.
 *
 * The default values here MUST match the Vue player defaults
 * (`EmbedPlayer.vue` / `entries/players/main.ts`) — `EMBED_OPTION_DEFAULTS`
 * is re-exported so the mount path can consume it directly.
 *
 * One page parameter rides beside the options through the same three consumers and is not one of
 * them: `preset`, the preset a code names (`PRESET_PARAM`).
 */

type OptionKind = 'boolean' | 'number' | 'string'

interface EmbedOptionDef<T = unknown> {
  /** URL query parameter and, with `_→-`, data-attribute name. */
  param: string
  /** Value below which the option is considered "not set" and omitted from outputs. */
  default: T
  kind: OptionKind
}

// Helpers keeping the map below tight and correctly typed.
const b = (param: string, def: boolean): EmbedOptionDef<boolean> => ({
  param,
  default: def,
  kind: 'boolean',
})
const n = (param: string, def: number | null): EmbedOptionDef<number | null> => ({
  param,
  default: def,
  kind: 'number',
})
const s = (param: string, def: string | null): EmbedOptionDef<string | null> => ({
  param,
  default: def,
  kind: 'string',
})

export const EMBED_OPTION_META = {
  // Playback / interactions
  autoPlay: b('auto_play', false),
  autoPlayNext: b('auto_play_next', true),
  loop: b('loop', false),
  muted: b('muted', false),
  shuffle: b('shuffle', false),
  controls: b('controls', true),
  pinControls: b('pin_controls', false),
  seeking: b('seeking', true),
  enableDownload: b('enable_download', false),

  // Chrome / UI toggles
  showChapters: b('show_chapters', true),
  showWaveform: b('show_waveform', true),
  showPlaylist: b('show_playlist', true),
  showHomepageInfo: b('show_homepage_info', true),
  showHomepagePlayButton: b('show_homepage_play_button', true),
  showEcoMode: b('show_eco_mode', true),
  showInfoButton: b('show_info_button', true),
  showThumbnails: b('show_thumbnails', false),
  showOutline: b('show_outline', false),
  showAds: b('show_ads', true),
  showLogo: b('show_logo', true),
  enablePip: b('enable_pip', true),
  enableFullscreen: b('enable_fullscreen', true),
  enableZoom: b('enable_zoom', true),
  enableSearch: b('enable_search', true),
  enableSlideshow: b('enable_slideshow', true),
  showCaptions: b('show_captions', true),

  // Enums / strings
  posterFit: s('poster_fit', 'contain'),
  collectionDisplayMode: s('collection_display_mode', 'drawer'),
  theme: s('theme', null),
  iconColor: s('icon_color', null),
  logoPosition: s('logo_position', null),
  logoClickUrl: s('logo_click_url', null),
  primaryColor: s('primary_color', null),

  // Numeric
  slideshowDuration: n('slideshow_duration', 5),
  startAt: n('start_at', null),
  // The entry a collection opens on, 0-based: a channel's page decides it, and the transcript's
  // link to an instant writes it past a collection's first entry.
  startIndex: n('start_index', 0),
  startPage: n('start_page', 1),
  logoOpacity: n('logo_opacity', null),
} as const satisfies Record<string, EmbedOptionDef>

export type EmbedOptionKey = keyof typeof EMBED_OPTION_META

/**
 * The page parameter naming the preset a code is configured by — `preset=<uid>` on the iframe,
 * `data-preset="<uid>"` on the loader's script (`docs/plans/embeds-rust.md` ER11).
 *
 * **Not in `EMBED_OPTION_META`, because it is not a player option**: the page reads it before it
 * resolves the look (first in the preset cascade), hands it back in its config, and the player
 * sends it on the metadata read — no prop draws it and no baseline is diffed against it. A code
 * carries it only when a person picked a preset for it, and the page ignores one that names no
 * live preset of the publication's organization written for its kind.
 */
export const PRESET_PARAM = 'preset'

/**
 * The page parameter carrying a `token_required` embed's access token — `token=<secret>` on the
 * iframe, `data-token="<secret>"` on the loader's script (`docs/plans/embeds-rust.md` ER4). The
 * page reads it before anything else (`embed/access.rs`) and the player sends it on the metadata
 * read.
 *
 * **Not in `EMBED_OPTION_META` either, and never diffed**: a code carries one only because it was
 * written for a holder of that token. It is readable in the host page's source whichever way it is
 * carried, so it identifies a partner and is revoked per partner; it does not stop a copy.
 */
export const TOKEN_PARAM = 'token'

/**
 * The referrer policy every embed iframe this product generates carries: the snippet's
 * (`useEmbedSnippet`), the loader's (`entries/loaders/main.ts`) and a channel item's code
 * (`ItemEmbedModal`). Here, beside the rest of the code's vocabulary, because the loader imports this
 * file and nothing with Vue in it.
 *
 * `no-referrer-when-downgrade`: the embed page's navigation `Referer` names the host page's full
 * address — path and query — where the browsers' default for a cross-origin frame
 * (`strict-origin-when-cross-origin`) sends its origin alone. The page signs it into its opening
 * grant, and the analytics keep its path, its UTMs and its search term. The previous platform's codes
 * carried `unsafe-url` for the same reason; for a target that is always HTTPS the two are identical,
 * and this one is not the name an audit flags (the owner's choice, 2026-09-19).
 */
export const EMBED_REFERRER_POLICY = 'no-referrer-when-downgrade'

/** A uuid, in any case — the only value `preset` is emitted or read back as. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** base64url, the alphabet `base_auth::token::mint_token` answers a secret in. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/

/**
 * `EmbedSrcOptions` is the public shape accepted by `buildEmbedSrc` and the
 * snippet generator. Keys mirror the Vue prop names so the JSON mount path
 * (`VideasEmbed.mount(el, opts)`) can share the same option names.
 */
export type EmbedSrcOptions = {
  [K in EmbedOptionKey]?:
    | (typeof EMBED_OPTION_META)[K]['default']
    | NonNullable<(typeof EMBED_OPTION_META)[K]['default']>
} & {
  /** The preset this code names — {@link PRESET_PARAM}. */
  preset?: string | null
  /** The access token this code opens with — {@link TOKEN_PARAM}. */
  token?: string | null
}

/** The preset `opts` names, when it is a uuid: the one form the page reads. */
function presetOf(opts: EmbedSrcOptions): string | undefined {
  const preset = typeof opts.preset === 'string' ? opts.preset.trim() : ''
  return UUID_PATTERN.test(preset) ? preset : undefined
}

/** The token `opts` carries, when it is one: anything else would only be refused by the page. */
function tokenOf(opts: EmbedSrcOptions): string | undefined {
  const token = typeof opts.token === 'string' ? opts.token.trim() : ''
  return TOKEN_PATTERN.test(token) ? token : undefined
}

/** Plain defaults object — consumed by the Vue mount helper for fallbacks. */
export const EMBED_OPTION_DEFAULTS: { [K in EmbedOptionKey]: EmbedSrcOptions[K] } =
  Object.fromEntries(
    (Object.entries(EMBED_OPTION_META) as [EmbedOptionKey, EmbedOptionDef][]).map(
      ([k, meta]) => [k, meta.default as EmbedSrcOptions[EmbedOptionKey]],
    ),
  ) as { [K in EmbedOptionKey]: EmbedSrcOptions[K] }

/**
 * Decide whether a concrete value differs enough from its "baseline" to be
 * emitted. The baseline is what the embed page resolves on its own when the
 * param is absent: by default the static `EMBED_OPTION_META.default`, but the
 * caller can pass a richer `baseline` (the server-resolved preset settings
 * merged over the per-asset-type defaults). Only true overrides relative to
 * that baseline are emitted, so a snippet for an embed that already has a
 * preset applied server-side stays clean.
 */
function isDiverging(
  key: EmbedOptionKey,
  value: unknown,
  baseline?: EmbedSrcOptions,
): boolean {
  const meta = EMBED_OPTION_META[key]
  if (value == null) return false
  const base =
    baseline && (baseline as Record<string, unknown>)[key] != null
      ? (baseline as Record<string, unknown>)[key]
      : meta.default
  if (meta.kind === 'number') {
    return typeof value === 'number' && value !== base
  }
  if (meta.kind === 'string') {
    if (typeof value !== 'string' || value === '') return false
    return value !== base
  }
  // boolean
  return typeof value === 'boolean' && value !== base
}

/** Canonical string serialization for URL params and data-* attributes. */
function serialize(key: EmbedOptionKey, value: unknown): string {
  if (EMBED_OPTION_META[key].kind === 'boolean') return value ? '1' : '0'
  if (key === 'primaryColor' && typeof value === 'string') {
    return value.replace('#', '')
  }
  return String(value)
}

/**
 * Iterate (key, value, serialized) triples for every option that diverges
 * from its baseline. Shared by URL builder + data-attr emitter. Pass
 * `baseline` (server-resolved preset settings over the per-type defaults) to
 * emit only real overrides; omit it to diff against the static defaults.
 */
export function* emittableOptions(
  opts: EmbedSrcOptions,
  baseline?: EmbedSrcOptions,
): Generator<{
  key: EmbedOptionKey | typeof PRESET_PARAM | typeof TOKEN_PARAM
  param: string
  value: string
}> {
  // The preset first: it decides what the page resolves before any option is laid over it, and
  // it is never diffed — a code names one only because somebody picked it.
  const preset = presetOf(opts)
  if (preset) yield { key: PRESET_PARAM, param: PRESET_PARAM, value: preset }
  const token = tokenOf(opts)
  if (token) yield { key: TOKEN_PARAM, param: TOKEN_PARAM, value: token }
  for (const key of Object.keys(EMBED_OPTION_META) as EmbedOptionKey[]) {
    const raw = (opts as Record<string, unknown>)[key]
    if (!isDiverging(key, raw, baseline)) continue
    yield { key, param: EMBED_OPTION_META[key].param, value: serialize(key, raw) }
  }
}

/**
 * Build the full embed iframe URL from a base URL and a set of user options.
 * Only options that diverge from their baseline are appended (see
 * `emittableOptions`).
 */
export function buildEmbedSrc(
  baseUrl: string,
  opts: EmbedSrcOptions,
  baseline?: EmbedSrcOptions,
): string {
  if (!baseUrl) return ''
  const params = new URLSearchParams()
  for (const { param, value } of emittableOptions(opts, baseline)) {
    params.set(param, value)
  }
  const qs = params.toString()
  return qs ? `${baseUrl}?${qs}` : baseUrl
}

/**
 * Parse an HTML element's `dataset` back into `EmbedSrcOptions`. Unknown keys
 * and unparseable values are silently dropped — same forgiving semantics as
 * the URL-param side.
 */
export function parseEmbedOptionsFromDataset(ds: DOMStringMap): EmbedSrcOptions {
  const out: Record<string, unknown> = {}
  const preset = presetOf({ preset: ds[PRESET_PARAM] })
  if (preset) out.preset = preset
  const token = tokenOf({ token: ds[TOKEN_PARAM] })
  if (token) out.token = token
  for (const key of Object.keys(EMBED_OPTION_META) as EmbedOptionKey[]) {
    const raw = ds[key as keyof DOMStringMap]
    if (raw == null || raw === '') continue
    const meta = EMBED_OPTION_META[key]
    if (meta.kind === 'boolean') {
      if (raw === '1' || raw === 'true') out[key] = true
      else if (raw === '0' || raw === 'false') out[key] = false
      continue
    }
    if (meta.kind === 'number') {
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) out[key] = parsed
      continue
    }
    out[key] = raw
  }
  return out as EmbedSrcOptions
}
